import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useEffect, useState, useRef } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

import {
  AppBar,
  Toolbar,
  IconButton,
  Icon,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Divider,
  Drawer,
  Modal,
  Box,
  Grid,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";
import { useAuth } from "context/AuthContext";
import Breadcrumbs from "examples/Breadcrumbs";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import { googleLogout } from "@react-oauth/google";
import { useDomain } from "context/DomainContext";
import { FAST_API_BASE_URL } from "constants";
import axios from "axios";
import { LAMBDA_BASE_URL } from "constants";
import axiosInstance from "utils/axiosConfig";
import encryptedStorage from "utils/encryptedStorage";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "msalConfig";
import { useSystemTheme } from "context/SystemThemeContext";

const SESSION_CONFIG = {
  SHORT_SESSION: 15 * 60 * 1000,
  EXTENDED_SESSION: 30 * 24 * 60 * 60 * 1000,
  WARNING_TIME: 2 * 60 * 1000,
};

const SessionWarningDialog = ({ open, countdown, onKeepSignedIn, onLogout, isMobile }) => {
  const totalSeconds = 120;
  const progress = (countdown / totalSeconds) * 100;
  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const isUrgent = countdown <= 30;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={() => {}}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
          maxWidth: 400,
          width: "100%",
          m: isMobile ? 2 : "auto",
          overflow: "hidden",
        },
      }}
    >
      {/* Urgent color top bar */}
      <Box
        sx={{
          height: 5,
          background: isUrgent
            ? "linear-gradient(90deg, #ef4444, #dc2626)"
            : "linear-gradient(90deg, #219297, #1a7a7d)",
          width: `${progress}%`,
          transition: "width 1s linear, background 0.5s ease",
        }}
      />

      <Box sx={{ p: isMobile ? 3 : 4, textAlign: "center" }}>
        {/* Icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            backgroundColor: isUrgent ? "#fef2f2" : "#f0fdfa",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2.5,
            border: `2px solid ${isUrgent ? "#fecaca" : "#ccfbf1"}`,
            transition: "all 0.5s ease",
          }}
        >
          <Icon
            sx={{
              fontSize: 36,
              color: isUrgent ? "#ef4444" : "#219297",
              transition: "color 0.5s ease",
            }}
          >
            {isUrgent ? "warning" : "schedule"}
          </Icon>
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          fontWeight="700"
          sx={{ mb: 1, color: "#1a1a1a", fontSize: isMobile ? "18px" : "20px" }}
        >
          Session Expiring Soon
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, fontSize: isMobile ? "13px" : "14px", lineHeight: 1.6 }}
        >
          You've been inactive. You'll be logged out in
        </Typography>

        {/* Circular countdown timer */}
        <Box sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={100}
            thickness={4}
            sx={{ color: "#e5e7eb", position: "absolute" }}
          />
          <CircularProgress
            variant="determinate"
            value={progress}
            size={100}
            thickness={4}
            sx={{
              color: isUrgent ? "#ef4444" : "#219297",
              transition: "color 0.5s ease",
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
                transition: "stroke-dashoffset 1s linear",
              },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h5"
              fontWeight="800"
              sx={{
                color: isUrgent ? "#ef4444" : "#219297",
                fontSize: "22px",
                lineHeight: 1,
                transition: "color 0.5s ease",
              }}
            >
              {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
            </Typography>
            <Typography variant="caption" sx={{ color: "#9ca3af", fontSize: "10px" }}>
              remaining
            </Typography>
          </Box>
        </Box>

        <Box display="flex" gap={1.5} flexDirection={isMobile ? "column" : "row"}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onLogout}
            sx={{
              py: 1.25,
              fontWeight: 600,
              textTransform: "none",
              fontSize: isMobile ? "13px" : "14px",
              color: "#6b7280",
              borderColor: "#d1d5db",
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: "#f9fafb",
                borderColor: "#9ca3af",
              },
            }}
          >
            Log Out
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={onKeepSignedIn}
            sx={{
              py: 1.25,
              fontWeight: 700,
              textTransform: "none",
              fontSize: isMobile ? "13px" : "14px",
              background: isUrgent
                ? "linear-gradient(135deg, #ef4444, #dc2626)"
                : "linear-gradient(135deg, #219297, #1a7a7d)",
              color: "#ffffff",
              borderRadius: "10px",
              boxShadow: isUrgent
                ? "0 4px 12px rgba(239,68,68,0.35)"
                : "0 4px 12px rgba(33,146,151,0.35)",
              transition: "all 0.5s ease",
              "&:hover": {
                background: isUrgent
                  ? "linear-gradient(135deg, #dc2626, #b91c1c)"
                  : "linear-gradient(135deg, #1a7a7d, #156266)",
                transform: "translateY(-1px)",
              },
            }}
            startIcon={<Icon sx={{ fontSize: 18 }}>refresh</Icon>}
          >
            Keep Me Signed In
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
function DashboardNavbar({ absolute = false, light = false, isMini = false }) {
  const [navbarType, setNavbarType] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addDomainModalOpen, setAddDomainModalOpen] = useState(false);
  const [domainRegistrationLoading, setDomainRegistrationLoading] = useState(false);
  const [domainFormData, setDomainFormData] = useState({
    domainName: "",
    websitePlatform: "",
    companyName: "",
  });

  const [signupDialogOpen, setSignupDialogOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [googleSignInLoading, setGoogleSignInLoading] = useState(false);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [signupFormData, setSignupFormData] = useState({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [isDomainCheckLoading, setIsDomainCheckLoading] = useState(false);
  const [domainUsersCount, setDomainUsersCount] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const showSessionWarningRef = useRef(false);
  const [sessionWarningTimer, setSessionWarningTimer] = useState(null);
  const [showStayLoggedInPrompt, setShowStayLoggedInPrompt] = useState(false);
  const [sessionWarningCountdown, setSessionWarningCountdown] = useState(120); // 2 minutes in seconds
  const warningCountdownRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";

  const { 
    user, 
    currentDomain, 
    sites, 
    login, 
    logout, 
    switchDomain, 
    updateSites, 
    isAuthenticated, 
    getAuthHeaders,
    loading: authLoading 
  } = useAuth();
  const { setSource } = useDomain();

  const location = useLocation();
  const route = location.pathname.split("/").slice(1);
  const navigate = useNavigate();
  const [microsoftSignInLoading, setMicrosoftSignInLoading] = useState(false);
      const { instance: msalInstance } = useMsal();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar } = controller;

  const isLoggedIn = isAuthenticated();
  const userEmail = user?.email || "";
  const userName = user?.name || "";
  const [firstName, lastName] = userName.split(' ').length >= 2 
    ? [userName.split(' ')[0], userName.split(' ').slice(1).join(' ')]
    : [userName, ''];


  const idleTimeout = 15 * 60 * 1000;
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const passwordRules = {
    letter: /[A-Za-z]/.test(signupFormData.password),
    uppercase: /[A-Z]/.test(signupFormData.password),
    lowercase: /[a-z]/.test(signupFormData.password),
    number: /[0-9]/.test(signupFormData.password),
    specialChar: /[^A-Za-z0-9]/.test(signupFormData.password),
    minLength: signupFormData.password.length >= 8,
  };

  const allRulesPassed = Object.values(passwordRules).every(Boolean);
  const initializeSession = (shouldRemember) => {
    const sessionConfig = {
      type: shouldRemember ? 'extended' : 'short',
      startTime: Date.now(),
      expiryTime: Date.now() + (shouldRemember ? SESSION_CONFIG.EXTENDED_SESSION : SESSION_CONFIG.SHORT_SESSION),
      lastActivity: Date.now(),
    };

    if (shouldRemember) {
      localStorage.setItem('sessionConfig', JSON.stringify(sessionConfig));
      localStorage.setItem('rememberMe', 'true');
    } else {
      sessionStorage.setItem('sessionConfig', JSON.stringify(sessionConfig));
      localStorage.setItem('rememberMe', 'false');
    }

    setRememberMe(shouldRemember);
  };

  const getSessionConfig = () => {
    const localConfig = localStorage.getItem('sessionConfig');
    const sessionConfig = sessionStorage.getItem('sessionConfig');

    if (localConfig) {
      return JSON.parse(localConfig);
    } else if (sessionConfig) {
      return JSON.parse(sessionConfig);
    }
    return null;
  };

  const isSessionValid = () => {
    const config = getSessionConfig();
    if (!config) return false;

    const now = Date.now();
    return now < config.expiryTime;
  };

  const extendSession = () => {
    const config = getSessionConfig();
    if (!config) return;

    const newConfig = {
      ...config,
      expiryTime: Date.now() + (config.type === 'extended' ? SESSION_CONFIG.EXTENDED_SESSION : SESSION_CONFIG.SHORT_SESSION),
      lastActivity: Date.now(),
    };

    if (config.type === 'extended') {
      localStorage.setItem('sessionConfig', JSON.stringify(newConfig));
    } else {
      sessionStorage.setItem('sessionConfig', JSON.stringify(newConfig));
    }

    // Clear the logout timer
    if (sessionWarningTimer) {
      clearTimeout(sessionWarningTimer);
      setSessionWarningTimer(null);
    }

    setShowSessionWarning(false);
    showSnackbar("Session extended successfully", "success");
  };

  const handleStayLoggedInResponse = (shouldStay) => {
    setShowStayLoggedInPrompt(false);

    if (shouldStay) {
      // Switch to extended session
      const newConfig = {
        type: 'extended',
        startTime: Date.now(),
        expiryTime: Date.now() + SESSION_CONFIG.EXTENDED_SESSION,
        lastActivity: Date.now(),
      };
      localStorage.setItem('sessionConfig', JSON.stringify(newConfig));
      localStorage.setItem('rememberMe', 'true');
      sessionStorage.removeItem('sessionConfig');
      setRememberMe(true);
      // showSnackbar("You will stay Signed in!", "success");
    } else {
      // Keep short session
      localStorage.setItem('rememberMe', 'false');
      // showSnackbar("You'll be logged out after 15 min of inactivity", "info");
    }

    // Clear the flag in case it's still there
    localStorage.removeItem('showStayLoggedInPrompt');
  };
  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleAutoLogout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (sessionWarningTimer) {
      clearTimeout(sessionWarningTimer);
      setSessionWarningTimer(null);
    }
    if (warningCountdownRef.current) {
      clearInterval(warningCountdownRef.current);
      warningCountdownRef.current = null;
    }

    showSnackbar("Session expired due to inactivity", "warning");
    googleLogout();
    logout();
    clearTokensFromEncryptedStorage();
    localStorage.removeItem('lastActivityTime');
    localStorage.removeItem('sessionConfig');
    sessionStorage.removeItem('sessionConfig');
    showSessionWarningRef.current = false;
    setShowSessionWarning(false);
    navigate("/login");
  };
  const handleKeepMeSignedIn = () => {
    if (warningCountdownRef.current) {
      clearInterval(warningCountdownRef.current);
      warningCountdownRef.current = null;
    }
    showSessionWarningRef.current = false;
    setShowSessionWarning(false);
    setSessionWarningCountdown(120);

    // Extend the session by another 15 minutes from now
    const currentConfig = getSessionConfig();
    if (currentConfig) {
      const updatedConfig = {
        ...currentConfig,
        lastActivity: Date.now(),
        expiryTime: Date.now() + SESSION_CONFIG.SHORT_SESSION,
      };
      sessionStorage.setItem('sessionConfig', JSON.stringify(updatedConfig));
    }
    updateLastActivityTime();
  };
  const updateLastActivityTime = () => {
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem('lastActivityTime', now.toString());
  };
  const resetIdleTimer = () => {
    updateLastActivityTime();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (isLoggedIn) {
      timeoutRef.current = setTimeout(() => {
        handleAutoLogout();
      }, idleTimeout);
    }
  };
  useEffect(() => {
  const response = window.__msalRedirectResponse;
  if (!response) return;

  window.__msalRedirectResponse = null;

const processMicrosoftRedirect = async () => {
    setMicrosoftSignInLoading(true);
    try {
      const { account, accessToken } = response;

      let profilePic = null;
      try {
        const graphRes = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (graphRes.ok) {
          profilePic = URL.createObjectURL(await graphRes.blob());
        }
      } catch (_) {}

      const domainToken = getDomainTokenFromQuery() || getDomainTokenFromStorage();
      
      const headers = {};
      if (domainToken) {
        headers["DOMAIN-TOKEN"] = domainToken;
      }

      const payload = {
        mail: account.username,
        name: account.name,
        profile_pic: profilePic,
        role: "",
        type: "microsoft",
      };

      let loginResponse;
      try {
        loginResponse = await axiosInstance.post(
          `${FAST_API_BASE_URL}/api/user/login`,
          payload,
          { headers }
        );
      } catch (loginError) {
        if (loginError.response?.status === 401) {
          const nameParts = account.name.split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          setSignupEmail(account.username);
          setSignupFormData({
            firstName,
            lastName,
            password: "",
            confirmPassword: "",
          });
          setShowSignupForm(true);
          setLoginDialogOpen(false);
          setSignupDialogOpen(true);
          return;
        }
        throw loginError;
      }

      if (loginResponse?.status === 200 && loginResponse.data) { 
        const { result } = loginResponse.data;
        
        if (result.role) {
          localStorage.setItem('role', result.role);
        }
        
        await handleLoginSuccess(
          {
            id: result.user_id,
            email: account.username,
            name: result.name,
            profile_pic: result.profile_pic || profilePic,
            is_super_admin: result.is_super_admin === 1,
            is_whitelabel_user: result.is_whitelabel_user,
            is_whitelabelling_reseller_only: result.is_whitelabelling_reseller_only || false,
          },
          result.domains || [],
          domainToken,
          true
        );
      }
    } catch (error) {
      console.error("Microsoft redirect processing error:", error);
      showSnackbar("Microsoft sign-in failed. Please try again.", "error");
    } finally {
      setMicrosoftSignInLoading(false);
    }
  };

  processMicrosoftRedirect();
}, []);

// Initialize remember me preference on mount
  useEffect(() => {
    const storedRememberMe = localStorage.getItem('rememberMe') === 'true';
    setRememberMe(storedRememberMe);
  }, []);
  
  // Smart session monitoring
  // Smart session monitoring - no warning dialog, just auto-logout
  useEffect(() => {
    if (!isLoggedIn) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (warningCountdownRef.current) {
        clearInterval(warningCountdownRef.current);
        warningCountdownRef.current = null;
      }
      return;
    }

    const config = getSessionConfig();

    // If no session config or using extended session (remember me), don't auto-logout
    if (!config || config.type === 'extended') {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    const WARNING_BEFORE_MS = 2 * 60 * 1000; // show warning 2 mins before expiry

    const startWarningCountdown = () => {
      setSessionWarningCountdown(120);
      showSessionWarningRef.current = true;
      setShowSessionWarning(true);

      if (warningCountdownRef.current) {
        clearInterval(warningCountdownRef.current);
      }

      warningCountdownRef.current = setInterval(() => {
        setSessionWarningCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(warningCountdownRef.current);
            warningCountdownRef.current = null;
            handleAutoLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    // Activity tracking for short sessions
    const activityEvents = [
      'mousedown', 'mousemove', 'keydown', 'scroll',
      'touchstart', 'click', 'input', 'focus',
      'wheel', 'resize'
    ];

    const handleActivity = () => {
      // If warning is already showing, don't reset on activity
      if (showSessionWarningRef.current) return;

      updateLastActivityTime();

      const currentConfig = getSessionConfig();
      if (currentConfig && currentConfig.type === 'short') {
        const updatedConfig = {
          ...currentConfig,
          lastActivity: Date.now(),
          expiryTime: Date.now() + SESSION_CONFIG.SHORT_SESSION,
        };
        sessionStorage.setItem('sessionConfig', JSON.stringify(updatedConfig));
      }
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Check session every 5 seconds
    const intervalId = setInterval(() => {
      const currentConfig = getSessionConfig();
      if (!currentConfig) {
        handleAutoLogout();
        return;
      }

      const now = Date.now();
      const timeUntilExpiry = currentConfig.expiryTime - now;

      if (timeUntilExpiry <= 0) {
        handleAutoLogout();
        return;
      }

      // Show warning when 2 minutes remain and warning isn't already shown
      if (timeUntilExpiry <= WARNING_BEFORE_MS && !showSessionWarningRef.current) {
        startWarningCountdown();
      }
    }, 5 * 1000);

    // Initial check
    const currentConfig = getSessionConfig();
    if (currentConfig) {
      const timeUntilExpiry = currentConfig.expiryTime - Date.now();
      if (timeUntilExpiry <= 0) {
        handleAutoLogout();
      } else if (timeUntilExpiry <= WARNING_BEFORE_MS && !showSessionWarningRef.current) {
        startWarningCountdown();
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      clearInterval(intervalId);
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isLoggedIn, location.pathname]);

  const getDomainTokenFromQuery = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const domainToken = queryParams.get('domain_id');
    return domainToken;
  };
  
  const getDomainTokenFromStorage = () => {
    try {
      const domainToken = encryptedStorage.getItem('url_domain_id');
      return domainToken;
    } catch (error) {
      console.error('Failed to get domain token from encrypted storage:', error);
      return null;
    }
  };

  const saveTokensToEncryptedStorage = (domainToken, secretToken) => {
    try {
      if (domainToken) {
        encryptedStorage.setItem('domain_token', domainToken);
      }
      if (secretToken) {
        encryptedStorage.setItem('secret', secretToken);
      }
      return true;
    } catch (error) {
      console.error('Failed to save tokens to encrypted storage:', error);
      return false;
    }
  };

  const clearTokensFromEncryptedStorage = () => {
    try {
      encryptedStorage.removeItem('domain_token');
      encryptedStorage.removeItem('secret');
      return true;
    } catch (error) {
      console.error('Failed to clear tokens from encrypted storage:', error);
      return false;
    }
  };

  const checkAndOpenAuthDialog = async (domainToken) => {
    if (!domainToken || isLoggedIn) return;
    
    setIsDomainCheckLoading(true);
    try {
      const res = await axios.get(
        `${LAMBDA_BASE_URL}/domain-users-count`,
        {
          headers: {
            "DOMAIN-TOKEN": domainToken,
          }
        }
      );
      if (res.data?.messages?.count !== undefined) {
        const count = res.data.messages.count;
        setDomainUsersCount(count);
        
        if (count === 0) {
          setSignupDialogOpen(true);
        } else {
          setLoginDialogOpen(true);
        }
      }
    } catch (error) {
      console.error("Failed to fetch domain users count:", error);
      setLoginDialogOpen(true);
    } finally {
      setIsDomainCheckLoading(false);
    }
  };

  useEffect(() => {
    const domainId = getDomainTokenFromQuery();
    if (domainId) {
      try {
        encryptedStorage.setItem('url_domain_id', domainId);
      } catch (error) {
        console.error('Failed to store URL domain_id:', error);
      }
    }
  }, []);
// Check if we should show the stay logged in prompt after login redirect
  useEffect(() => {
    if (!isLoggedIn) return;
    
    const shouldShowPrompt = localStorage.getItem('showStayLoggedInPrompt') === 'true';
    const currentRememberMe = localStorage.getItem('rememberMe') === 'true';
    
    // Only show if flag is set, user is logged in, and not already using remember me
    if (shouldShowPrompt && !currentRememberMe) {
      // Small delay to ensure navbar is fully rendered
      const timer = setTimeout(() => {
        setShowStayLoggedInPrompt(true);
        // Clear the flag so it doesn't show again
        localStorage.removeItem('showStayLoggedInPrompt');
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn, location.pathname]);

  const getInstallSource = () => {
    try {
      const raw = localStorage.getItem("install_source");
      if (!raw) return null;
    
      const parsed = JSON.parse(raw);
      const THIRTY_MINUTES = 30 * 60 * 1000;
    
      if (Date.now() - parsed.timestamp > THIRTY_MINUTES) {
        localStorage.removeItem("install_source");
        return null;
      }
    
      return parsed.value;
    } catch {
      localStorage.removeItem("install_source");
      return null;
    }
  };
  const handleLoginSuccess = async (userData, domains, domainToken = null,shouldRemember=false ) => {
    login(userData, domains);
    fetchSites();
    initializeSession(shouldRemember);
    const urlDomainId = getDomainTokenFromQuery() || getDomainTokenFromStorage();
    
    if (urlDomainId && domains && domains.length > 0) {
      const matchingDomain = domains.find(
        d => d.domain_token === urlDomainId || d.domain_id === urlDomainId
      );
      
      if (matchingDomain) {
        saveTokensToEncryptedStorage(matchingDomain.domain_token, null);
        switchDomain(matchingDomain.domain_token);
        setSource(matchingDomain.source);
        if (matchingDomain.role) {
          localStorage.setItem('role', matchingDomain.role);
        }
      } else {
        const firstDomainToken = domains[0].domain_token || domains[0].domain_id;
        if (firstDomainToken) {
          saveTokensToEncryptedStorage(firstDomainToken, null);
          switchDomain(firstDomainToken);
          if (domains[0]?.source) {
            setSource(domains[0].source);
          }
          if (domains[0]?.role) {
            localStorage.setItem('role', domains[0].role);
          }
        }
      }
    } else if (domains && domains.length > 0) {
      const firstDomainToken = domains[0].domain_token || domains[0].domain_id;
      if (firstDomainToken) {
        saveTokensToEncryptedStorage(firstDomainToken, null);
        switchDomain(firstDomainToken);
        if (domains[0]?.source) {
          setSource(domains[0].source);
        }
        if (domains[0]?.role) {
          localStorage.setItem('role', domains[0].role);
        }
      }
    }

    setLoginDialogOpen(false);
    setSignupDialogOpen(false);
    updateLastActivityTime();
    resetIdleTimer();
    
    window.dispatchEvent(new CustomEvent('auth-state-changed'));
    
    showSnackbar("Successfully logged in!", "success");
    
    setTimeout(() => {
      const installSource = getInstallSource();
    
      if (installSource === "hubspot_marketplace") {
        localStorage.removeItem("install_source");
        navigate("/integrations?tab=hubspot");
        return;
      }
    
      let targetPath = "/dashboard";
    
      if (userData.is_super_admin) {
        targetPath = "/customers";
      } 
      else if (domains && domains.length > 0 && domains[0].role?.toLowerCase() === "read only") {
        targetPath = "/dashboard";
      } 
      else if (domains && domains.length > 0 && domains[0].role?.toLowerCase() === "admin") {
        targetPath = "/settings";
      }
      else if (userData.role?.toLowerCase() === "admin") {
        targetPath = "/settings";
      }
    
      if (!location.pathname.includes(targetPath)) {
        navigate(targetPath);
      }
    }, 1500);
  };

  useEffect(() => {
    const handleOpenLoginDialog = (event) => {
      const domainToken = getDomainTokenFromQuery();
      
      if (domainToken) {
        setLoginDialogOpen(true);
      } else {
        const storedDomainToken = getDomainTokenFromStorage();
        if (storedDomainToken) {
          setLoginDialogOpen(true);
        } else {
          setLoginDialogOpen(true);
        }
      }
    };

    const handleOpenSignupDialog = (event) => {
      const domainToken = getDomainTokenFromQuery();
      
      if (domainToken) {
        setSignupDialogOpen(true);
      } else {
        const storedDomainToken = getDomainTokenFromStorage();
        if (storedDomainToken) {
          setSignupDialogOpen(true);
        } else {
          setSignupDialogOpen(true);
        }
      }
    };

    window.addEventListener('open-login-dialog', handleOpenLoginDialog);
    window.addEventListener('open-signup-dialog', handleOpenSignupDialog);
    
    const domainToken = getDomainTokenFromQuery() || getDomainTokenFromStorage();
    if (domainToken && !isLoggedIn) {
      checkAndOpenAuthDialog(domainToken);
    }

    return () => {
      window.removeEventListener('open-login-dialog', handleOpenLoginDialog);
      window.removeEventListener('open-signup-dialog', handleOpenSignupDialog);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (signupDialogOpen) {
      setShowOtpField(false);
      setShowSignupForm(false);
      setShowPasswordField(false);
      setSignupEmail("");
      setOtp("");
      setLoginPassword("");
      setSignupFormData({
        firstName: "",
        lastName: "",
        password: "",
        confirmPassword: "",
      });
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [signupDialogOpen]);

  useEffect(() => {
    if (loginDialogOpen) {
      setSignupEmail("");
      setLoginPassword("");
      setShowPassword(false);
    }
  }, [loginDialogOpen]);

  const websitePlatforms = [
    { value: "wordpress", label: "WordPress" },
    { value: "webflow", label: "Webflow" },
    { value: "wix", label: "Wix" },
    { value: "squarespace", label: "Squarespace" },
    { value: "custom", label: "Others" },
  ];

  useEffect(() => {
    // const fetchSites = async () => {
    //   try {
    //     const headers = getAuthHeaders();
    //     const res = await axiosInstance.get(`${FAST_API_BASE_URL}/api/user/domains`, {
    //       headers
    //     });

    //     if (res.data?.result && Array.isArray(res.data.result.domains)) {
    //       updateSites(res.data.result.domains);
    //     }
    //   } catch (error) {
    //     console.error("Failed to fetch sites:", error);
    //   }
    // };

    if (isLoggedIn) {
      fetchSites();
    }
  }, []);

  useEffect(() => {
    const type = fixedNavbar ? "sticky" : "static";
    setNavbarType(type);

    const handleTransparentNavbar = () => {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    };

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  useEffect(() => {
    if (sites.length === 0) return;
    const urlDomainId = getDomainTokenFromQuery() || getDomainTokenFromStorage();
    if (urlDomainId) {
      const matchingDomain = sites.find(
        site => site.domain_token === urlDomainId || site.domain_id === urlDomainId
      );
      
      if (matchingDomain) {
        if (!currentDomain || currentDomain.domain_token !== matchingDomain.domain_token) {
          switchDomain(matchingDomain.domain_token);
          setSource(matchingDomain.source);
          if (matchingDomain.role?.toLowerCase() === "admin" && route[0] != "settings") {
            navigate("/settings");}
          if (matchingDomain.role?.toLowerCase() === "read only" && route[0] !== "dashboard") {
            navigate("/dashboard");
          }
          if (matchingDomain.role == null) {
            navigate("/customers");
          }
        }
        return;
      } else {
        // console.log('No matching domain found for URL domain_id');
      }
    }
    if (currentDomain) {
      const isValid = sites.some((site) => site.domain_token === currentDomain.domain_token);
      if (!isValid && sites.length > 0) {
        switchDomain(sites[0].domain_token);
        setSource(sites[0].source);
      }
      return;
    }
    const firstSite = sites[0];
    // console.log('Switching to first site:', firstSite);
    if (firstSite) {
      switchDomain(firstSite.domain_token);
      setSource(firstSite.source);

    if (firstSite.role?.toLowerCase() === "admin" && route[0] != "settings") {
        navigate("/settings");}
      if (firstSite.role?.toLowerCase() === "read only" && route[0] !== "dashboard") {
        navigate("/dashboard");
      }
      if (firstSite.role == null) {
        navigate("/customers");
      }
    }
  }, [sites, currentDomain, switchDomain, setSource, navigate, route])
const [domainSearchTerm, setDomainSearchTerm] = useState("");

// Add this filter function before the return statement
const getFilteredDomains = () => {
  if (!domainSearchTerm.trim()) return sites;
  const searchLower = domainSearchTerm.toLowerCase().trim();
  return sites.filter(site => 
    site.domain_name.toLowerCase().includes(searchLower)
  );
};
  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (sessionWarningTimer) {
      clearTimeout(sessionWarningTimer);
      setSessionWarningTimer(null);
    }

    localStorage.removeItem('lastActivityTime');
    localStorage.removeItem('sessionConfig');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('sessionConfig');

    googleLogout();
    logout();
    clearTokensFromEncryptedStorage();
    handleMenuClose();
    showSessionWarningRef.current = false;
    setShowSessionWarning(false);
    navigate("/login");
  };

  const getSelectedSiteName = () => {
    return currentDomain?.domain_name || "Select Site";
  };

  const handleLogin = () => {
    navigate(`/login`);
  };


  const handleMicrosoftSignIn = async () => {
    setMicrosoftSignInLoading(true);
    try {
      await msalInstance.loginRedirect({
        ...loginRequest,
        redirectUri: window.location.origin,
      });
    } catch (error) {
      if (error?.errorCode !== "user_cancelled") {
        console.error("Microsoft login initiation error:", error);
        showSnackbar("Failed to start Microsoft sign-in. Please try again.", "error");
      }
      setMicrosoftSignInLoading(false);
    }
  };

  const handleGoogleSignIn = async (credentialResponse) => {
    setGoogleSignInLoading(true);
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const domainToken = getDomainTokenFromQuery() || getDomainTokenFromStorage();
      
      const headers = {};
      if (domainToken) {
        headers["DOMAIN-TOKEN"] = domainToken;
      }

      const payload = {
        mail: decoded.email,
        name: decoded.name,
        profile_pic: decoded.picture,
        role: "",
        type: "google",
      };

      const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/login`, payload, {
        headers,
      });

      if (response.status === 200 && response.data) {
        const { result } = response.data;
        if(result.role){
          localStorage.setItem('role', result.role);
        }
        await handleLoginSuccess(
          {
            id: result.user_id,
            email: decoded.email,
            name: result.name,
            is_super_admin: result.is_super_admin === 1,
            is_whitelabel_user: result.is_whitelabel_user
          },
          result.domains || [],
          domainToken,
          true // Google login defaults to remember me
        );

      }
    } catch (error) {
      console.error("Google login error:", error);
      if (error.response && error.response.status === 401) {
        const decoded = jwtDecode(credentialResponse.credential);
        const nameParts = decoded.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        setSignupEmail(decoded.email);
        setSignupFormData({
          firstName: firstName,
          lastName: lastName,
          password: "",
          confirmPassword: "",
        });
        setShowSignupForm(true);
        setLoginDialogOpen(false);
        setSignupDialogOpen(true);
      } else {
        showSnackbar("Google sign-in failed. Please try again.", "error");
      }
    } finally {
      setGoogleSignInLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupEmail || !signupEmail.includes('@')) {
      showSnackbar("Please enter a valid email address", "error");
      return;
    }

    setSignupLoading(true);

    try {
      const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/signup`, {
        email: signupEmail,
      }, {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        setShowOtpField(true);
        setShowPasswordField(false);
        showSnackbar("Verification code sent to your email!", "success");
      }
    } catch (error) {
      console.error("Signup failed:", error);
      
      if (error.response && error.response.status === 409) {
        setShowPasswordField(true);
        setShowOtpField(false);
      } else {
        showSnackbar(error.response?.data?.messages?.[0] || "Signup failed. Please try again.", "error");
      }
    } finally {
      setSignupLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    
    if (!loginPassword) {
      showSnackbar("Please enter your password", "error");
      return;
    }

    setLoginLoading(true);

    try {
      const domainToken = getDomainTokenFromQuery() || getDomainTokenFromStorage();
      
      const headers = {};
      if (domainToken) {
        headers["DOMAIN-TOKEN"] = domainToken;
      }

      const payload = {
        mail: signupEmail,
        password: loginPassword,
        type: "custom",
        role:"admin"
      };

      const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/login`, payload, {
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200 && response.data) {
        const { result } = response.data;
        if(result.role){
          localStorage.setItem('role', result.role);
        }
        if (!rememberMe) {
  localStorage.setItem('showStayLoggedInPrompt', 'true');
}
        await handleLoginSuccess(
          {
            id: result.user_id,
            email: signupEmail,
            name: result.name,
            is_super_admin: result.is_super_admin === 1,
            is_whitelabel_user: result.is_whitelabel_user
          },
          result.domains || [],
          domainToken
        );
      }
    } catch (error) {
      console.error("Password login error:", error);
      showSnackbar(error.response?.data?.messages?.[0] || "Invalid credentials. Please try again.", "error");
    } finally {
      setLoginLoading(false);
    }
  };

const handleLoginFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupEmail || !loginPassword) {
      showSnackbar("Please enter email and password", "error");
      return;
    }

    setLoginLoading(true);

    try {
      const domainToken = getDomainTokenFromQuery() || getDomainTokenFromStorage();
      
      const headers = {};
      if (domainToken) {
        headers["DOMAIN-TOKEN"] = domainToken;
      }

      const payload = {
        mail: signupEmail,
        password: loginPassword,
        type: "custom",
        role: "admin"
      };

      const payload2 = {
        mail: signupEmail,
        password: loginPassword,
        type: "custom",
        role: "",
      };

      let response;
      if (Object.keys(headers).length > 0) {
        response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/login`, payload, {
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
        });
      } else {
        response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/re-login`, payload2);
      }

      if (response.status === 200 && response.data) {
        const { result } = response.data;
        if(result.role){
          localStorage.setItem('role', result.role);
        }
        
        // Set flag to show stay logged in prompt if user didn't check "Keep me signed in"
        if (!rememberMe) {
          localStorage.setItem('showStayLoggedInPrompt', 'true');
        }
        
        await handleLoginSuccess(
          {
            id: result.user_id,
            email: signupEmail,
            name: result.name,
            is_super_admin: result.is_super_admin === 1,
            is_whitelabel_user: result.is_whitelabel_user
          },
          result.domains || [],
          domainToken,
          rememberMe // Use checkbox value
        );
      }
    } catch (error) {
      console.error("Login error:", error);
      showSnackbar(error.response?.data?.messages?.[0] || "Invalid credentials. Please try again.", "error");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length < 4) {
      showSnackbar("Please enter a valid OTP", "error");
      return;
    }

    setOtpLoading(true);

    try {
      const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/verify-otp`, {
        email: signupEmail,
        otp: otp,
      }, {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        showSnackbar("Email verified successfully!", "success");
        setShowSignupForm(true);
        setShowOtpField(false);
      }
    } catch (error) {
      console.error("OTP verification failed:", error);
      showSnackbar("Invalid OTP. Please try again.", "error");
    } finally {
      setOtpLoading(false);
    }
  };
const fetchSites = async () => {
      try {
        const headers = getAuthHeaders();
        const res = await axiosInstance.get(`${FAST_API_BASE_URL}/api/user/domains`, {
          headers
        });

        if (res.data?.result && Array.isArray(res.data.result.domains)) {
          updateSites(res.data.result.domains);
        }
      } catch (error) {
        console.error("Failed to fetch sites:", error);
      }
    };

  const handleSignupFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupFormData.password || !signupFormData.confirmPassword) {
      showSnackbar("Please fill in all password fields", "error");
      return;
    }

    if (signupFormData.password !== signupFormData.confirmPassword) {
      showSnackbar("Passwords do not match!", "error");
      return;
    }

    if (!allRulesPassed) {
      showSnackbar("Password does not meet the required criteria", "error");
      return;
    }

    setSignupLoading(true);

    try {
      const domainToken = getDomainTokenFromQuery() || getDomainTokenFromStorage();
      
      const payload = {
        password: signupFormData.password,
        email: signupEmail,
        user_name: `${signupFormData.firstName} ${signupFormData.lastName}`.trim(),
        role: "admin",
      };

      const headers = {};
      if (domainToken) {
        headers["DOMAIN-TOKEN"] = domainToken;
      }

      const response = await axiosInstance.post(
        `${FAST_API_BASE_URL}/api/save-password`,
        payload,
        {
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        showSnackbar("Account created successfully! Redirecting...", "success");
        const loginResponse = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/login`, {
          mail: signupEmail,
          password: signupFormData.password,
          type: "custom",
          role: "admin"
        }, {
          headers
        });

        if (loginResponse.status === 200) {
          const { result } = loginResponse.data;
          if(result.role){
            localStorage.setItem('role', result.role);
          }
          
          await handleLoginSuccess(
            {
              id: result.user_id,
              email: signupEmail,
              name: `${signupFormData.firstName} ${signupFormData.lastName}`.trim(),
              is_super_admin: result.is_super_admin === 1,
              is_whitelabel_user: result.is_whitelabel_user
            },
            result.domains || [],
            domainToken,
            true // New signups default to remember me
          );
        }
      }
    } catch (err) {
      console.error("Final signup failed:", err);
      showSnackbar("Failed to create account! Please try again.", "error");
    } finally {
      setSignupLoading(false);
    }
  };

  const extractDomainName = (input) => {
    if (!input) return '';
    
    let domain = input.trim().toLowerCase();
    domain = domain.replace(/^https?:\/\//, '');
    domain = domain.split('/')[0];
    domain = domain.split(':')[0];
    
    if (domain.startsWith('www.')) {
      domain = domain.substring(4);
    }
    
    return domain;
  };

  const handleDomainFormChange = (e) => {
    const { name, value } = e.target;
    setDomainFormData({
      ...domainFormData,
      [name]: value,
    });
  };

  const handleDomainSelectChange = (name, value) => {
    setDomainFormData({
      ...domainFormData,
      [name]: value,
    });
  };

  const handleAddDomainSubmit = async (e) => {
    e.preventDefault();

    if (!domainFormData.domainName || !domainFormData.websitePlatform || !domainFormData.companyName) {
      showSnackbar("Please fill in all fields", "error");
      return;
    }

    const cleanDomain = extractDomainName(domainFormData.domainName);

    if (cleanDomain === "abc.com") {
      showSnackbar("Please contact your admin to add user!", "warning");
      return;
    }

    setDomainRegistrationLoading(true);

    try {
      const payload1 = {
        domain_url: cleanDomain,
        source: domainFormData.websitePlatform.value,
        email: userEmail,
        first_name: firstName,
        last_name: lastName,
      };
      
      const payload = {
        domain: cleanDomain,
        source: domainFormData.websitePlatform.value,
        company_name: domainFormData.companyName.trim(),
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        date_formate: "MM-DD-YYYY",
        email: userEmail,
        user_name: `${firstName} ${lastName}`,
        is_google_user: false,
        is_user_logged_in: true
      };
      
      const response1 = await axiosInstance.post(`${LAMBDA_BASE_URL}/save-registration`, payload1, {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
      });
      
      if (domainFormData.websitePlatform.value === "custom" || domainFormData.websitePlatform.value === "wix" || domainFormData.websitePlatform.value === "squarespace") {
        const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/register`, payload, {
          headers: {
            "X-API-KEY": process.env.REACT_APP_API_KEY,
            "Content-Type": "application/json",
          },
        });

        if(response.status === 200){
          const sitesResponse = await axiosInstance.get(`${FAST_API_BASE_URL}/api/user/domains`, {
            headers: getAuthHeaders()
          });
          
          if (sitesResponse.data?.result && Array.isArray(sitesResponse.data.result.domains)) {
            updateSites(sitesResponse.data.result.domains);
          }
          
          navigate("/settings");
        }
      }
      
      if (response1.status === 200) {
        showSnackbar("Domain added successfully!", "success");
        setAddDomainModalOpen(false);
        setDomainFormData({
          domainName: "",
          websitePlatform: "",
          companyName: "",
        });
        const sitesResponse = await axiosInstance.get(`${FAST_API_BASE_URL}/api/user/domains`, {
          headers: getAuthHeaders()
        });
        
        if (sitesResponse.data?.result && Array.isArray(sitesResponse.data.result.domains)) {
          updateSites(sitesResponse.data.result.domains);
          setDrawerOpen(true);
        }

        if (domainFormData.websitePlatform.value === "wordpress") {
          window.open(`https://wordpress.org/plugins/ansera-search/`, '_blank');
        } else if (domainFormData.websitePlatform.value === "webflow") {
          window.open("https://webflow.com/apps/detail/ansera-search", '_blank');
        }
      }
    } catch (err) {
      console.error("Domain registration failed:", err);
      showSnackbar("Domain registration failed. Please try again.", "error");
    } finally {
      setDomainRegistrationLoading(false);
    }
  };

  const openAddDomainModal = () => {
    setDrawerOpen(false);
    setAddDomainModalOpen(true);
  };

  const renderRule = (label, passed) => ( 
    <Box display="flex" alignItems="center" mb={1}> 
      {passed ? ( 
        <CheckCircleIcon sx={{ mr: 1, color: "#219297", fontSize: "16px" }} />
      ) : ( 
        <CancelIcon color="error" sx={{ mr: 1, fontSize: "16px" }} /> 
      )} 
      <Typography variant="body2" color={passed ? "#219297" : "text.secondary"} sx={{ fontSize: "13px" }}> 
        {label} 
      </Typography> 
    </Box>
  );
  
const renderSignupForm = () => (
  <form onSubmit={handleSignupFormSubmit}>
    <Box>
      <Box 
        sx={{ 
          mb: 3,
          p: isMobile ? 1.5 : 2,
          backgroundColor: isDarkMode ? "#0a1020" : "#f0fdfa",
          borderRadius: "10px",
          border: `1px solid ${isDarkMode ? "#2d2d3f" : "#ccfbf1"}`,
          transition: "background-color 0.3s ease, border-color 0.3s ease",
        }}
      >
        <Typography variant="body2" sx={{ fontSize: isMobile ? "13px" : "14px", mb: 1, color: isDarkMode ? "#a0aec0" : "text.secondary" }}>
          Completing signup for
        </Typography>
        <Typography variant="body1" fontWeight="600" sx={{ color: "#219297", fontSize: isMobile ? "14px" : "16px" }}>
          {signupEmail}
        </Typography>
      </Box>
      
      <Grid container spacing={isMobile ? 1 : 2} mb={3}>
        <Grid item xs={12} sm={6}>
          <Box mb={2}>
            <label 
              htmlFor="firstName" 
              style={{ 
                fontSize: isMobile ? "13px" : "14px", 
                fontWeight: "600", 
                marginBottom: "8px", 
                display: "block",
                color: isDarkMode ? "#e2e8f0" : "#374151"
              }}
            >
              First Name
            </label>
            <input
              id="firstName"
              type="text"
              value={signupFormData.firstName}
              onChange={(e) => setSignupFormData(prev => ({...prev, firstName: e.target.value}))}
              required
              placeholder="First Name"
              style={{
                width: "100%",
                padding: isMobile ? "10px 12px" : "12px 14px",
                fontSize: isMobile ? "13px" : "14px",
                border: `1px solid ${isDarkMode ? "#4a5568" : "#ccc"}`,
                borderRadius: "8px",
                boxSizing: "border-box",
                backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
                color: isDarkMode ? "#e2e8f0" : "inherit",
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box mb={2}>
            <label 
              htmlFor="lastName" 
              style={{ 
                fontSize: isMobile ? "13px" : "14px", 
                fontWeight: "600", 
                marginBottom: "8px", 
                display: "block",
                color: isDarkMode ? "#e2e8f0" : "#374151"
              }}
            >
              Last Name
            </label>
            <input
              id="lastName"
              type="text"
              value={signupFormData.lastName}
              onChange={(e) => setSignupFormData(prev => ({...prev, lastName: e.target.value}))}
              required
              placeholder="Last Name"
              style={{
                width: "100%",
                padding: isMobile ? "10px 12px" : "12px 14px",
                fontSize: isMobile ? "13px" : "14px",
                border: `1px solid ${isDarkMode ? "#4a5568" : "#ccc"}`,
                borderRadius: "8px",
                boxSizing: "border-box",
                backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
                color: isDarkMode ? "#e2e8f0" : "inherit",
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Box mb={2} sx={{ position: "relative" }}>
        <label 
          htmlFor="password" 
          style={{ 
            fontSize: isMobile ? "13px" : "14px", 
            fontWeight: "600", 
            marginBottom: "8px", 
            display: "block",
            color: isDarkMode ? "#e2e8f0" : "#374151"
          }}
        >
          Password
        </label>
        <input
          id="password"
          type={showPassword ? "text" : "password"}
          value={signupFormData.password}
          onChange={(e) => setSignupFormData(prev => ({...prev, password: e.target.value}))}
          required
          placeholder="Enter password"
          style={{
            width: "100%",
            padding: isMobile ? "10px 36px 10px 12px" : "12px 40px 12px 14px",
            fontSize: isMobile ? "13px" : "14px",
            border: `1px solid ${isDarkMode ? "#4a5568" : "#ccc"}`,
            borderRadius: "8px",
            boxSizing: "border-box",
            backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
            color: isDarkMode ? "#e2e8f0" : "inherit",
          }}
        />
        <IconButton
          onClick={() => setShowPassword(!showPassword)}
          sx={{
            position: "absolute",
            right: "8px",
            top: isMobile ? "30px" : "34px",
            padding: "6px",
          }}
          size={isMobile ? "small" : "medium"}
        >
          {showPassword ? <VisibilityOff fontSize={isMobile ? "small" : "medium"} sx={{ color: isDarkMode ? "#a0aec0" : undefined }} /> : <Visibility fontSize={isMobile ? "small" : "medium"} sx={{ color: isDarkMode ? "#a0aec0" : undefined }} />}
        </IconButton>
      </Box>

      {signupFormData.password && (
        <Box mb={2} sx={{ mt: 1 }}>
          {renderRule("At least 8 characters", passwordRules.minLength)}
          {renderRule("Includes a number", passwordRules.number)}
          {renderRule("Includes a special character", passwordRules.specialChar)}
          {renderRule("Includes Uppercase [A-Z]", passwordRules.uppercase)}
          {renderRule("Includes Lowercase [a-z]", passwordRules.lowercase)}
        </Box>
      )}

      <Box mb={3} sx={{ position: "relative" }}>
        <label 
          htmlFor="confirmPassword" 
          style={{ 
            fontSize: isMobile ? "13px" : "14px", 
            fontWeight: "600", 
            marginBottom: "8px", 
            display: "block",
            color: isDarkMode ? "#e2e8f0" : "#374151"
          }}
        >
          Confirm Password
        </label>
        <input
          id="confirmPassword"
          type={showConfirmPassword ? "text" : "password"}
          value={signupFormData.confirmPassword}
          onChange={(e) => setSignupFormData(prev => ({...prev, confirmPassword: e.target.value}))}
          required
          placeholder="Confirm password"
          style={{
            width: "100%",
            padding: isMobile ? "10px 36px 10px 12px" : "12px 40px 12px 14px",
            fontSize: isMobile ? "13px" : "14px",
            border: `1px solid ${isDarkMode ? "#4a5568" : "#ccc"}`,
            borderRadius: "8px",
            boxSizing: "border-box",
            backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
            color: isDarkMode ? "#e2e8f0" : "inherit",
          }}
        />
        <IconButton
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          sx={{
            position: "absolute",
            right: "8px",
            top: isMobile ? "30px" : "34px",
            padding: "6px",
          }}
          size={isMobile ? "small" : "medium"}
        >
          {showConfirmPassword ? <VisibilityOff fontSize={isMobile ? "small" : "medium"} sx={{ color: isDarkMode ? "#a0aec0" : undefined }} /> : <Visibility fontSize={isMobile ? "small" : "medium"} sx={{ color: isDarkMode ? "#a0aec0" : undefined }} />}
        </IconButton>
      </Box>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={signupLoading || !allRulesPassed}
        sx={{
          py: isMobile ? 1.25 : 1.75,
          backgroundColor: (!allRulesPassed) ? (isDarkMode ? "#2d2d3f" : "#ccc") : "#219297",
          color: "#ffffff",
          borderRadius: "10px",
          fontWeight: 600,
          fontSize: isMobile ? "14px" : "15px",
          textTransform: "none",
          boxShadow: allRulesPassed ? "0 4px 12px rgba(33, 146, 151, 0.3)" : "none",
          "&:hover": {
            backgroundColor: allRulesPassed ? "#1a7a7d" : (isDarkMode ? "#2d2d3f" : "#ccc"),
            boxShadow: allRulesPassed ? "0 6px 16px rgba(33, 146, 151, 0.4)" : "none",
          },
          "&:disabled": {
            backgroundColor: isDarkMode ? "#2d2d3f" : "#cbd5e1",
            boxShadow: "none",
            color: isDarkMode ? "#a0aec0" : undefined,
          },
        }}
      >
        {signupLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
            {isMobile ? "Creating..." : "Creating Account..."}
          </Box>
        ) : (
          "Complete Signup"
        )}
      </Button>
    </Box>
  </form>
);

const renderLoginForm = () => (
  <form onSubmit={handleLoginFormSubmit}>
    <Box>
      <Box mb={3}>
        <label 
          htmlFor="login-email" 
          style={{ 
            fontSize: isMobile ? "13px" : "14px", 
            fontWeight: "600", 
            marginBottom: "8px", 
            display: "block",
            color: isDarkMode ? "#e2e8f0" : "#374151"
          }}
        >
          Email Address
        </label>
        <input
          id="login-email"
          type="email"
          value={signupEmail}
          onChange={(e) => setSignupEmail(e.target.value)}
          required
          placeholder="you@example.com"
          style={{
            width: "100%",
            padding: isMobile ? "12px 14px" : "14px 16px",
            fontSize: isMobile ? "14px" : "15px",
            border: `2px solid ${isDarkMode ? "#4a5568" : "#e5e7eb"}`,
            borderRadius: "10px",
            boxSizing: "border-box",
            outline: "none",
            transition: "all 0.2s ease",
            fontFamily: "inherit",
            backgroundColor: isDarkMode ? "#2d2d3f" : "#fafafa",
            color: isDarkMode ? "#e2e8f0" : "inherit",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "#219297";
            e.target.style.backgroundColor = isDarkMode ? "#3d3d4f" : "#ffffff";
            e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDarkMode ? "#4a5568" : "#e5e7eb";
            e.target.style.backgroundColor = isDarkMode ? "#2d2d3f" : "#fafafa";
            e.target.style.boxShadow = "none";
          }}
        />
      </Box>

      <Box mb={2} sx={{ position: "relative" }}>
        <label 
          htmlFor="login-password" 
          style={{ 
            fontSize: isMobile ? "13px" : "14px", 
            fontWeight: "600", 
            marginBottom: "8px", 
            display: "block",
            color: isDarkMode ? "#e2e8f0" : "#374151"
          }}
        >
          Password
        </label>
        <input
          id="login-password"
          type={showPassword ? "text" : "password"}
          value={loginPassword}
          onChange={(e) => setLoginPassword(e.target.value)}
          required
          placeholder="Enter your password"
          style={{
            width: "100%",
            padding: isMobile ? "10px 36px 10px 12px" : "12px 40px 12px 14px",
            fontSize: isMobile ? "13px" : "14px",
            border: `2px solid ${isDarkMode ? "#4a5568" : "#e5e7eb"}`,
            borderRadius: "10px",
            boxSizing: "border-box",
            outline: "none",
            transition: "all 0.2s ease",
            backgroundColor: isDarkMode ? "#2d2d3f" : "#fafafa",
            color: isDarkMode ? "#e2e8f0" : "inherit",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "#219297";
            e.target.style.backgroundColor = isDarkMode ? "#3d3d4f" : "#ffffff";
            e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDarkMode ? "#4a5568" : "#e5e7eb";
            e.target.style.backgroundColor = isDarkMode ? "#2d2d3f" : "#fafafa";
            e.target.style.boxShadow = "none";
          }}
        />
        <IconButton
          onClick={() => setShowPassword(!showPassword)}
          sx={{
            position: "absolute",
            right: "8px",
            top: isMobile ? "30px" : "34px",
            padding: "6px",
          }}
          size={isMobile ? "small" : "medium"}
        >
          {showPassword ? <VisibilityOff fontSize={isMobile ? "small" : "medium"} sx={{ color: isDarkMode ? "#a0aec0" : undefined }} /> : <Visibility fontSize={isMobile ? "small" : "medium"} sx={{ color: isDarkMode ? "#a0aec0" : undefined }} />}
        </IconButton>
      </Box>

      <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center">
          <input
            type="checkbox"
            id="rememberMeLogin"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            style={{
              width: "16px",
              height: "16px",
              marginRight: "8px",
              cursor: "pointer",
              accentColor: "#219297",
            }}
          />
          <label 
            htmlFor="rememberMeLogin" 
            style={{ 
              fontSize: isMobile ? "12px" : "13px", 
              color: isDarkMode ? "#e2e8f0" : "#374151",
              cursor: "pointer",
              userSelect: "none"
            }}
          >
            Keep me signed in
          </label>
        </Box>
      </Box>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loginLoading}
        sx={{
          py: isMobile ? 1.25 : 1.75,
          backgroundColor: "#219297",
          color: "#ffffff",
          borderRadius: "10px",
          fontWeight: 600,
          fontSize: isMobile ? "14px" : "15px",
          textTransform: "none",
          boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
          "&:hover": {
            backgroundColor: "#1a7a7d",
            boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
          "&:disabled": {
            backgroundColor: isDarkMode ? "#2d2d3f" : "#cbd5e1",
            boxShadow: "none",
            color: isDarkMode ? "#a0aec0" : undefined,
          },
          transition: "all 0.2s ease",
        }}
      >
        {loginLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
            {isMobile ? "Logging in..." : "Logging in..."}
          </Box>
        ) : (
          "Sign In"
        )}
      </Button>

      <Box sx={{ my: isMobile ? 2 : 3, display: "flex", alignItems: "center" }}>
        <Divider sx={{ flexGrow: 1, borderColor: isDarkMode ? "#2d2d3f" : undefined }} />
        <Typography variant="body2" sx={{ px: 2, color: isDarkMode ? "#a0aec0" : "text.secondary", fontSize: isMobile ? "12px" : "14px" }}>
          OR
        </Typography>
        <Divider sx={{ flexGrow: 1, borderColor: isDarkMode ? "#2d2d3f" : undefined }} />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <GoogleLogin
            theme="filled_blue"
            text="continue_with"
            onSuccess={handleGoogleSignIn}
            onError={() => console.log("Google login failed")}
            size={isMobile ? "medium" : "large"}
          />
        </Box>

        {googleSignInLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
            <CircularProgress size={isMobile ? 20 : 24} sx={{ color: "#219297" }} />
            <Typography variant="body2" sx={{ fontSize: isMobile ? "12px" : "14px", color: isDarkMode ? "#a0aec0" : "#666" }}>
              Signing in with Google...
            </Typography>
          </Box>
        )}

        {/* <Button
          onClick={handleMicrosoftSignIn}
          disabled={microsoftSignInLoading}
          variant="outlined"
          startIcon={
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
              width="18"
              alt="Microsoft"
              // style={{ filter: isDarkMode ? "brightness(0) invert(1)" : "none" }}
            />
          }
          sx={{
            textTransform: "none",
            fontWeight: 500,
            fontSize: isMobile ? "13px" : "14px",
            color: isDarkMode ? "#e2e8f0" : "#3c4043",
            borderColor: isDarkMode ? "#4a5568" : "#dadce0",
            backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
            borderRadius: "8px",
            py: isMobile ? 1.25 : 1.5,
            "&:hover": {
              backgroundColor: isDarkMode ? "#3d3d4f" : "#f5f5f5",
              borderColor: isDarkMode ? "#718096" : "#c0c0c0",
            },
          }}
        >
          Continue with Microsoft
        </Button> */}
        
        {microsoftSignInLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} sx={{ color: "#219297" }} />
            <Typography variant="body2" sx={{ fontSize: isMobile ? "12px" : "14px", color: isDarkMode ? "#e2e8f0" : "#666" }}>
              Signing in with Microsoft...
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ textAlign: "center", mt: 2 }}>
        <Typography variant="body2" sx={{ fontSize: isMobile ? "12px" : "14px", color: isDarkMode ? "#a0aec0" : "text.secondary" }}>
          Don't have an account?{" "}
          <Button
            variant="text"
            onClick={() => {
              setLoginDialogOpen(false);
              setSignupDialogOpen(true);
            }}
            sx={{
              color: "#219297",
              textTransform: "none",
              fontWeight: 600,
              fontSize: isMobile ? "12px" : "14px",
              p: 0,
              minWidth: "auto",
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            Sign up
          </Button>
        </Typography>
      </Box>
    </Box>
  </form>
);
  
const renderAuthButtons = () => {
  if (isLoggedIn) return null;

  if (isDomainCheckLoading) {
    return (
      <Button
        variant="outlined"
        disabled
        sx={{
          color: "#219297",
          borderColor: "#219297",
          borderRadius: "8px",
          fontWeight: 600,
          fontSize: "0.875rem",
          px: 3,
          py: 1,
          textTransform: "none",
          backgroundColor: "transparent",
          opacity: 0.7,
        }}
      >
        Checking...
      </Button>
    );
  }

  const domainToken = getDomainTokenFromQuery() || getDomainTokenFromStorage();
  if (!domainToken) {
    return (
      <Button
        variant="outlined"
        onClick={handleLogin}
        sx={{
          color: "#219297",
          borderColor: "#219297",
          borderRadius: "8px",
          fontWeight: 600,
          fontSize: "0.875rem",
          px: 3,
          py: 1,
          textTransform: "none",
          backgroundColor: "transparent",
          "&:hover": {
            borderColor: "#219297",
            color: "#219297",
            backgroundColor: isDarkMode ? "rgba(33, 146, 151, 0.15)" : "rgba(25, 118, 210, 0.04)",
          },
        }}
      >
        Login
      </Button>
    );
  }
  
  return (
    <Box display="flex" gap={1}>
      <Button
        variant="outlined"
        onClick={() => setLoginDialogOpen(true)}
        sx={{
          color: "#219297",
          borderColor: "#219297",
          borderRadius: "8px",
          fontWeight: 600,
          fontSize: "0.875rem",
          px: 2,
          py: 0.75,
          textTransform: "none",
          backgroundColor: "transparent",
          "&:hover": {
            borderColor: "#219297",
            color: "#219297",
            backgroundColor: isDarkMode ? "rgba(33, 146, 151, 0.15)" : "rgba(33, 146, 151, 0.04)",
          },
        }}
      >
        Login
      </Button>
      
      <Button
        variant="contained"
        onClick={() => setSignupDialogOpen(true)}
        sx={{
          color: "white",
          backgroundColor: "#219297",
          borderRadius: "8px",
          fontWeight: 600,
          fontSize: "0.875rem",
          px: 2,
          py: 0.75,
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#1a7a7d",
            boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
          },
        }}
      >
        Sign Up
      </Button>
    </Box>
  );
};


const StayLoggedInPrompt = () => {
  return (
    <Snackbar
      open={showStayLoggedInPrompt}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      sx={{ mt: 8, mr: 2 }}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={() => setShowStayLoggedInPrompt(false)}
        icon={false}
        sx={{
          backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
          color: isDarkMode ? "#e2e8f0" : "#1a1a1a",
          alignItems: "flex-start",
          minWidth: isMobile ? "300px" : "380px",
          maxWidth: "400px",
          boxShadow: isDarkMode 
            ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0,0,0,0.2)"
            : "0 8px 32px rgba(33, 146, 151, 0.15), 0 2px 8px rgba(0,0,0,0.08)",
          borderRadius: "16px",
          border: `1px solid ${isDarkMode ? "rgba(33, 146, 151, 0.25)" : "rgba(33, 146, 151, 0.12)"}`,
          p: isMobile ? 2 : 2.5,
          "& .MuiAlert-message": {
            width: "100%",
            padding: 0,
          },
          "& .MuiAlert-action": {
            padding: 0,
            paddingLeft: 0,
            marginRight: 0,
            alignItems: "flex-start",
            marginTop: "-4px",
          },
          backdropFilter: "blur(10px)",
          background: isDarkMode 
            ? "linear-gradient(135deg, #060C18 0%, #0a1020 100%)"
            : "linear-gradient(135deg, #ffffff 0%, #f8fffe 100%)",
        }}
        action={
          <IconButton
            size="small"
            onClick={() => setShowStayLoggedInPrompt(false)}
            sx={{
              color: "#9ca3af",
              "&:hover": {
                backgroundColor: "rgba(156, 163, 175, 0.1)",
                color: "#6b7280",
              },
            }}
          >
            <Icon fontSize="small">close</Icon>
          </IconButton>
        }
      >
        <Box sx={{ width: "100%" }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #219297 0%, #1a7a7d 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(33, 146, 151, 0.25)",
              }}
            >
              <Icon sx={{ color: "#fff", fontSize: 20 }}>password</Icon>
            </Box>
            <Box flex={1}>
              <Typography 
                variant="body1" 
                fontWeight="700" 
                sx={{ 
                  fontSize: isMobile ? "15px" : "16px",
                  color: isDarkMode ? "#e2e8f0" : "#1a1a1a",
                  lineHeight: 1.3,
                }}
              >
                Stay signed in?
              </Typography>
            </Box>
          </Box>

          <Box display="flex" gap={1.5} flexDirection={isMobile ? "column" : "row"}>
            <Button
              size="medium"
              onClick={() => handleStayLoggedInResponse(false)}
              sx={{
                flex: 1,
                fontWeight: 600,
                textTransform: "none",
                fontSize: isMobile ? "13px" : "14px",
                py: 1.25,
                px: 2.5,
                color: isDarkMode ? "#a0aec0" : "#6b7280",
                backgroundColor: isDarkMode ? "#2d2d3f" : "#f3f4f6",
                borderRadius: "10px",
                border: `1px solid ${isDarkMode ? "#4a5568" : "#e5e7eb"}`,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: isDarkMode ? "#3d3d4f" : "#e5e7eb",
                  borderColor: isDarkMode ? "#4a5568" : "#d1d5db",
                  boxShadow: "none",
                },
                transition: "all 0.2s ease",
              }}
            >
              Not now
            </Button>
            <Button
              size="medium"
              variant="contained"
              onClick={() => handleStayLoggedInResponse(true)}
              sx={{
                flex: 1,
                fontWeight: 600,
                textTransform: "none",
                fontSize: isMobile ? "13px" : "14px",
                py: 1.25,
                px: 2.5,
                background: "linear-gradient(135deg, #219297 0%, #1a7a7d 100%)",
                color: "#ffffff",
                borderRadius: "10px",
                boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
                border: "none",
                "&:hover": {
                  background: "linear-gradient(135deg, #1a7a7d 0%, #156266 100%)",
                  boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                transition: "all 0.2s ease",
              }}
              startIcon={<Icon sx={{ fontSize: 18 }}>check_circle</Icon>}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Alert>
    </Snackbar>
  );
};
  return (
    <>
    <StayLoggedInPrompt />
     <SessionWarningDialog
      open={showSessionWarning}
      countdown={sessionWarningCountdown}
      onKeepSignedIn={handleKeepMeSignedIn}
      onLogout={handleLogout}
      isMobile={isMobile}
    />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#fff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#219297"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : snackbarSeverity === "warning"
                ? "#ed6c02"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <AppBar
        position={navbarType}
        color="inherit"
        sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
      >
        <Toolbar sx={(theme) => navbarContainer(theme)}>
          <SoftBox
            color="inherit"
            mb={{ xs: 1, md: 0 }}
            sx={(theme) => navbarRow(theme, { isMini })}
          >
            <Breadcrumbs
              icon="home"
              title={route[route.length - 1]}
              route={route}
              light={light}
            />
            <Icon
              fontSize="medium"
              sx={navbarDesktopMenu}
              onClick={handleMiniSidenav}
            >
              {miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </SoftBox>

          {!isMini && (
            <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
              <SoftBox
                color={light ? "white" : "inherit"}
                display="flex"
                alignItems="center"
                gap={1}
              >
                <IconButton
                  size="small"
                  color="inherit"
                  sx={navbarMobileMenu}
                  onClick={handleMiniSidenav}
                >
                  <Icon className={light ? "text-white" : "text-dark"}>
                    {miniSidenav ? "menu_open" : "menu"}
                  </Icon>
                </IconButton>

                {isLoggedIn ? (
                  <>
                    <SoftBox>
                      <Button
                        variant="outlined"
                        onClick={() => setDrawerOpen(true)}
                        sx={{
                          color: "#000",
                          borderColor: "#000",
                          borderRadius: "8px",
                          fontWeight: 500,
                          fontSize: isMobile ? "0.75rem" : "0.875rem",
                          px: isMobile ? 1.5 : 2,
                          py: isMobile ? 0.75 : 1,
                          textTransform: "none",
                          backgroundColor: "#fff",
                          "&:hover": {
                            borderColor: "teal",
                            color: "teal",
                            backgroundColor: "#f0fdfa",
                          },
                          "&.Mui-focused, &:focus, &:active": {
                            borderColor: "teal",
                            color: "teal",
                            backgroundColor: "#e0f7f7",
                          },
                        }}
                      >
                        {getSelectedSiteName()}
                      </Button>
                    </SoftBox>

                    <Drawer
                      anchor="right"
                      open={drawerOpen}
                      onClose={() => {
                        setDrawerOpen(false);
                        setDomainSearchTerm("");
                      }}
                      PaperProps={{
                        sx: {
                          width: isMobile ? 280 : 300,
                          maxWidth: "85vw",
                          backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
                          transition: "background-color 0.3s ease",
                        },
                      }}
                    >
                      <SoftBox p={isMobile ? 2 : 3} display="flex" flexDirection="column" height="100%">
                        <SoftBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <SoftTypography 
                            variant={isMobile ? "h6" : "h5"} 
                            fontWeight="bold"
                            sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}
                          >
                            Select a Site
                          </SoftTypography>
                          <IconButton 
                            onClick={() => {
                              setDrawerOpen(false);
                              setDomainSearchTerm("");
                            }} 
                            size={isMobile ? "small" : "medium"}
                            sx={{
                              color: isDarkMode ? "#e2e8f0" : undefined,
                              "&:hover": {
                                backgroundColor: isDarkMode ? "#2d2d3f" : undefined,
                              },
                            }}
                          >
                            <Icon>close</Icon>
                          </IconButton>
                        </SoftBox>
                          
                        <MenuItem
                          onClick={openAddDomainModal}
                          sx={{
                            mb: 2,
                            py: isMobile ? 1 : 1.5,
                            px: isMobile ? 1.5 : 2,
                            borderRadius: "8px",
                            backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
                            border: "1px solid #219297",
                            color: "#219297",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            "&:hover": {
                              backgroundColor: "#1a7a7d",
                              color: "#ffffff",
                            },
                          }}
                        >
                          <Icon sx={{ color: "#219287" }}>add</Icon>
                          <span style={{ color: "inherit" }}>Add Domain</span>
                        </MenuItem>
                        
                        <SoftBox sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              backgroundColor: isDarkMode ? "#1a1a2e" : "#f5f5f5",
                              borderRadius: "8px",
                              px: 1.5,
                              py: 0.75,
                              transition: "all 0.2s ease",
                              border: `1px solid ${isDarkMode ? "#2d2d3f" : "#e5e7eb"}`,
                              "&:focus-within": {
                                borderColor: "#219297",
                                boxShadow: "0 0 0 2px rgba(33, 146, 151, 0.1)",
                              },
                            }}
                          >
                            <Icon
                              sx={{
                                color: isDarkMode ? "#a0aec0" : "#9ca3af",
                                fontSize: isMobile ? 18 : 20,
                                mr: 1,
                              }}
                            >
                              search
                            </Icon>
                            <input
                              type="text"
                              placeholder="Search domain..."
                              value={domainSearchTerm}
                              onChange={(e) => setDomainSearchTerm(e.target.value)}
                              style={{
                                flex: 1,
                                background: "transparent",
                                border: "none",
                                outline: "none",
                                padding: isMobile ? "8px 0" : "10px 0",
                                fontSize: isMobile ? "13px" : "14px",
                                color: isDarkMode ? "#e2e8f0" : "#1a1a1a",
                              }}
                            />
                            {domainSearchTerm && (
                              <IconButton
                                size="small"
                                onClick={() => setDomainSearchTerm("")}
                                sx={{
                                  p: 0.5,
                                  color: isDarkMode ? "#a0aec0" : "#9ca3af",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: isDarkMode ? "#e2e8f0" : "#666",
                                  },
                                }}
                              >
                                <Icon sx={{ fontSize: isMobile ? 16 : 18 }}>close</Icon>
                              </IconButton>
                            )}
                          </Box>
                        </SoftBox>
                          
                        <SoftBox
                          sx={{
                            flex: 1,
                            overflowY: "auto",
                            overflowX: "hidden",
                            "&::-webkit-scrollbar": {
                              width: "4px",
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: isDarkMode ? "#1a1a2e" : "#f0f0f0",
                              borderRadius: "4px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: isDarkMode ? "#4a5568" : "#cfcfcf",
                              borderRadius: "4px",
                              "&:hover": {
                                backgroundColor: isDarkMode ? "#6b7a9a" : "#aaa",
                              },
                            },
                          }}
                        >
                          {getFilteredDomains().length === 0 ? (
                            <SoftBox
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                py: 4,
                                textAlign: "center",
                              }}
                            >
                              <Icon
                                sx={{
                                  fontSize: 40,
                                  color: isDarkMode ? "#4a5568" : "#d1d5db",
                                  mb: 1,
                                }}
                              >
                                search_off
                              </Icon>
                              <SoftTypography
                                variant="body2"
                                sx={{
                                  color: isDarkMode ? "#a0aec0" : "#9ca3af",
                                  fontSize: isMobile ? "12px" : "13px",
                                }}
                              >
                                No domains found matching "{domainSearchTerm}"
                              </SoftTypography>
                            </SoftBox>
                          ) : (
                            <>
                              {domainSearchTerm && (
                                <SoftTypography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    mb: 1,
                                    px: 1,
                                    color: isDarkMode ? "#a0aec0" : "#6b7280",
                                    fontSize: isMobile ? "11px" : "12px",
                                  }}
                                >
                                  {getFilteredDomains().length} {getFilteredDomains().length === 1 ? "result" : "results"} found
                                </SoftTypography>
                              )}

                              {getFilteredDomains().map((site) => {
                                const isSelected = currentDomain?.domain_token === site.domain_token;
                                if (site.is_deleted === 1 && user?.is_super_admin !== true) return null;
                                const highlightMatch = (text, searchTerm) => {
                                  if (!searchTerm) return text;
                                  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                                  const parts = text.split(regex);
                                  return parts.map((part, i) => 
                                    regex.test(part) ? 
                                      <span key={i} style={{ 
                                        backgroundColor: "#219297", 
                                        color: "#fff",
                                        borderRadius: "2px",
                                        padding: "0 2px"
                                      }}>{part}</span> : 
                                      part
                                  );
                                };

                                return (
                                  <MenuItem
                                    key={site.domain_token}
                                    onClick={() => {
                                      switchDomain(site.domain_token);
                                      setSource(site.source);
                                      setDrawerOpen(false);
                                      setDomainSearchTerm("");
                                      localStorage.setItem("role", site.role || "");
                                      if(site.role === "admin" && route[0] != "settings"){
                                        navigate("/settings");
                                      }
                                      if (site.role?.toLowerCase() === "read only" && route[0] !== "dashboard") {
                                        navigate("/dashboard");
                                      }
                                      if (site.role == null) {
                                        navigate("/dashboard");
                                      }
                                    }}
                                    sx={{
                                      mb: 1,
                                      py: isMobile ? 1 : 1.5,
                                      px: isMobile ? 1.5 : 2,
                                      borderRadius: "8px",
                                      backgroundColor: isSelected 
                                        ? "#219297" 
                                        : "transparent",
                                      fontWeight: isSelected ? "bold" : "normal",
                                      color: isSelected 
                                        ? "#ffffff" 
                                        : (isDarkMode ? "#e2e8f0" : "#000000"),
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      "&:hover": {
                                        backgroundColor: isSelected 
                                          ? "#1a767a" 
                                          : (isDarkMode ? "#2d2d3f" : "#e0f7fa"),
                                      },
                                      transition: "all 0.2s ease",
                                    }}
                                  >
                                    <Icon sx={{ color: isSelected ? "#ffffff" : "#219297" }}>public</Icon>
                                    <span
                                      style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: isMobile ? "160px" : "180px",
                                        display: "inline-block",
                                        fontSize: isMobile ? "14px" : "16px",
                                        color: "inherit",
                                      }}
                                    >
                                      {domainSearchTerm ? highlightMatch(site.domain_name, domainSearchTerm) : site.domain_name}
                                    </span>
                                  </MenuItem>
                                );
                              })}
                            </>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </Drawer>

                    <IconButton
                      onClick={handleMenuOpen}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                    <Avatar
                      src={user?.profile_pic || ""}
                      alt={userName}
                      sx={{ 
                        width: isMobile ? 32 : 36, 
                        height: isMobile ? 32 : 36, 
                        bgcolor: user?.profile_pic ? "transparent" : "#219297",
                        color: user?.profile_pic ? undefined : "white",
                        fontSize: isMobile ? "14px" : "16px",
                        fontWeight: "bold"
                      }}
                    >
                      {!user?.profile_pic && userName.charAt(0).toUpperCase()}
                    </Avatar>
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      PaperProps={{
                        elevation: 4,
                        sx: {
                          mt: 1.5,
                          borderRadius: 2,
                          overflow: "visible",
                          filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.15))",
                          minWidth: isMobile ? 240 : 260,
                          p: 1,
                          "& .MuiAvatar-root": {
                            width: isMobile ? 40 : 48,
                            height: isMobile ? 40 : 48,
                            ml: 1,
                            mr: 1.5,
                          },
                          "&:before": {
                            content: '""',
                            display: "block",
                            position: "absolute",
                            top: 0,
                            right: 20,
                            width: 10,
                            height: 10,
                            bgcolor: "background.paper",
                            transform: "translateY(-50%) rotate(45deg)",
                            zIndex: 0,
                          },
                        },
                      }}
                      transformOrigin={{
                        horizontal: "right",
                        vertical: "top",
                      }}
                      anchorOrigin={{
                        horizontal: "right",
                        vertical: "bottom",
                      }}
                    >
                      <MenuItem disableRipple sx={{ py: isMobile ? 1 : 1.5 }}>
                        <Avatar
                          src={user?.profile_pic || ""}
                          sx={{
                            bgcolor: "#219297",
                            color: "white",
                            fontSize: "16px",
                            fontWeight: "bold"
                          }}
                        >
                          {userName.charAt(0).toUpperCase()}
                        </Avatar>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: isMobile ? "14px" : "16px" }}>
                            {userName}
                          </div>
                          <div style={{ fontSize: isMobile ? "0.75rem" : "0.85rem", color: "#555" }}>
                            {userEmail}
                          </div>
                        </div>
                      </MenuItem>
                      <Divider sx={{ my: 1 }} />
                      <MenuItem
                        onClick={handleLogout}
                        sx={{ fontWeight: 500, alignContent: "center", fontSize: isMobile ? "14px" : "16px" }}
                      >
                        <Icon sx={{ fontSize: isMobile ? 16 : 18, mr: 1 }}>logout</Icon> Sign out
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  renderAuthButtons()
                )}
              </SoftBox>
            </SoftBox>
          )}
        </Toolbar>
      </AppBar>
      <Dialog
        open={signupDialogOpen}
        onClose={() => setSignupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            margin: isMobile ? "16px" : "32px",
            maxHeight: isMobile ? "calc(100% - 32px)" : "calc(100% - 64px)",
            display: "flex",
            flexDirection: "column",
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(4px)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 2 : 3,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            width: "100%",
            maxWidth: isMobile ? "calc(100vw - 32px)" : "500px",
            overflow: "hidden",
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          pt: isMobile ? 3 : 4, 
          px: isMobile ? 3 : 4,
          flexShrink: 0,
        }}>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
            <Box
              sx={{
                width: isMobile ? 48 : 64,
                height: isMobile ? 48 : 64,
                borderRadius: "50%",
                backgroundColor: "#e0f7f7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Icon sx={{ fontSize: isMobile ? 24 : 32, color: "#219297" }}>
                {showPasswordField ? "lock" : showOtpField ? "lock" : showSignupForm ? "person" : "mail"}
              </Icon>
            </Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ mb: 1, color: isDarkMode?"#ffffff" :"#1a1a1a" }}>
              {showSignupForm 
                ? "Complete Your Signup" 
                : showPasswordField
                ? "Welcome Back"
                : showOtpField 
                ? "Verify Your Email" 
                : "Welcome!"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ 
              maxWidth: 380,
              fontSize: isMobile ? "13px" : "14px",
            }}>
              {showSignupForm 
                ? "Set up your password to complete registration"
                : showPasswordField
                ? "Enter your password to continue"
                : showOtpField 
                ? "Enter the verification code we sent to your email"
                : "Sign up to access your dashboard"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          px: isMobile ? 3 : 4, 
          pb: isMobile ? 3 : 4, 
          pt: isMobile ? 1 : 3,
          overflow: "auto",
          flex: 1,
        }}>
          {showSignupForm ? (
            renderSignupForm()
          ) : showPasswordField ? (
            <form onSubmit={handlePasswordLogin}>
              <Box>
                <Box 
                  sx={{ 
                    mb: 3,
                    p: isMobile ? 1.5 : 2,
                    backgroundColor: "#f0fdfa",
                    borderRadius: "10px",
                    border: "1px solid #ccfbf1",
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "13px" : "14px", mb: 1 }}>
                    Welcome back! Logging in as
                  </Typography>
                  <Typography variant="body1" fontWeight="600" sx={{ color: "#219297", fontSize: isMobile ? "14px" : "16px" }}>
                    {signupEmail}
                  </Typography>
                </Box>
                
                <Box mb={3} sx={{ position: "relative" }}>
                  <label 
                    htmlFor="login-password" 
                    style={{ 
                      fontSize: isMobile ? "13px" : "14px", 
                      fontWeight: "600", 
                      marginBottom: "8px", 
                      display: "block",
                      color: "#374151"
                    }}
                  >
                    Password
                  </label>
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                    placeholder="Enter your password"
                    style={{
                      width: "100%",
                      padding: isMobile ? "10px 36px 10px 12px" : "12px 40px 12px 14px",
                      fontSize: isMobile ? "13px" : "14px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "10px",
                      boxSizing: "border-box",
                      outline: "none",
                      transition: "all 0.2s ease",
                      backgroundColor: "#fafafa",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#219297";
                      e.target.style.backgroundColor = "#ffffff";
                      e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.backgroundColor = "#fafafa";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{
                      position: "absolute",
                      right: "8px",
                      top: isMobile ? "30px" : "34px",
                      padding: "6px",
                    }}
                    size={isMobile ? "small" : "medium"}
                  >
                    {showPassword ? <VisibilityOff fontSize={isMobile ? "small" : "medium"} /> : <Visibility fontSize={isMobile ? "small" : "medium"} />}
                  </IconButton>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loginLoading}
                  sx={{
                    py: isMobile ? 1.25 : 1.75,
                    backgroundColor: "#219297",
                    color: "#ffffff",
                    borderRadius: "10px",
                    fontWeight: 600,
                    fontSize: isMobile ? "14px" : "15px",
                    textTransform: "none",
                    boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
                    "&:hover": {
                      backgroundColor: "#1a7a7d",
                      boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&:disabled": {
                      backgroundColor: "#cbd5e1",
                      boxShadow: "none",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  {loginLoading ? (
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
                      {isMobile ? "Logging in..." : "Logging in..."}
                    </Box>
                  ) : (
                    "Login"
                  )}
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setShowPasswordField(false);
                    setLoginPassword("");
                    setSignupEmail("");
                  }}
                  sx={{
                    mt: 2,
                    color: "#219297",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: isMobile ? "12px" : "14px",
                    "&:hover": {
                      backgroundColor: "#f0fdfa",
                    },
                  }}
                >
                  ← Change Email Address
                </Button>
              </Box>
            </form>
          ) : !showOtpField ? (
            <>
              <form onSubmit={handleSignupSubmit}>
                <Box>
                  <Box mb={3}>
                    <label 
                      htmlFor="signup-email" 
                      style={{ 
                        fontSize: isMobile ? "13px" : "14px", 
                        fontWeight: "600", 
                        marginBottom: "8px", 
                        display: "block",
                        color: "#374151"
                      }}
                    >
                      Email Address
                    </label>
                    <input
                      id="signup-email"
                      type="email"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      style={{
                        width: "100%",
                        padding: isMobile ? "12px 14px" : "14px 16px",
                        fontSize: isMobile ? "14px" : "15px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        boxSizing: "border-box",
                        outline: "none",
                        transition: "all 0.2s ease",
                        fontFamily: "inherit",
                        backgroundColor: "#fafafa",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#219297";
                        e.target.style.backgroundColor = "#ffffff";
                        e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e5e7eb";
                        e.target.style.backgroundColor = "#fafafa";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </Box>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={signupLoading}
                    sx={{
                      py: isMobile ? 1.25 : 1.75,
                      backgroundColor: "#219297",
                      color: "#ffffff",
                      borderRadius: "10px",
                      fontWeight: 600,
                      fontSize: isMobile ? "14px" : "15px",
                      textTransform: "none",
                      boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
                      "&:hover": {
                        backgroundColor: "#1a7a7d",
                        boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
                        transform: "translateY(-1px)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                      "&:disabled": {
                        backgroundColor: "#cbd5e1",
                        boxShadow: "none",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {signupLoading ? (
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <CircularProgress size={20} sx={{ color: "teal", mr: 1.5 }} />
                        {isMobile ? "Verifying..." : "Verifying..."}
                      </Box>
                    ) : (
                      "Continue with Email"
                    )}
                  </Button>
                </Box>
              </form>

              <Box sx={{ my: isMobile ? 2 : 3, display: "flex", alignItems: "center" }}>
                <Divider sx={{ flexGrow: 1 }} />
                <Typography variant="body2" sx={{ px: 2, color: "text.secondary", fontSize: isMobile ? "12px" : "14px" }}>
                  OR
                </Typography>
                <Divider sx={{ flexGrow: 1 }} />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <GoogleLogin
                  theme="filled_blue"
                  text="continue_with"
                  onSuccess={handleGoogleSignIn}
                  onError={() => console.log("Google login failed")}
                  size={isMobile ? "medium" : "large"}
                />
              </Box>

              {googleSignInLoading && (
                <Box sx={{ display:"flex", justifyContent:"center", mt: 2 }}>
                  <CircularProgress size={isMobile ? 20 : 24} />
                  <Typography variant="body2" sx={{ ml: 1, fontSize: isMobile ? "12px" : "14px" }}>
                    Signing in with Google...
                  </Typography>
                </Box>
              )}
                    
            {/* <Box item sx={{ display: "flex", justifyContent: "center",mt:1 }}>
              <Button
                onClick={handleMicrosoftSignIn}
                disabled={microsoftSignInLoading}
                variant="outlined"
                startIcon={
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                    width="18"
                    alt="Microsoft"
                  />
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "14px",
                  color: isDarkMode ? "#e2e8f0" : "#3c4043",
                  borderColor: isDarkMode ? "#4a5568" : "#dadce0",
                  backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
                  borderRadius: "4px",
                  px: 2,
                  py: 1,
                  "&:hover": {
                    backgroundColor: isDarkMode ? "#3d3d4f" : "#f5f5f5",
                    borderColor: isDarkMode ? "#718096" : "#c0c0c0",
                  },
                }}
              >
                Continue with Microsoft
              </Button>
              {microsoftSignInLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <CircularProgress size={24} sx={{ color: "#219297" }} />
                  <Typography variant="body2" sx={{ ml: 1, color: isDarkMode ? "#e2e8f0" : undefined }}>
                    Signing in with Microsoft...
                  </Typography>
                </Box>
              )}
            </Box> */}
            </>
          ) : (
            <form onSubmit={handleOtpSubmit}>
              <Box>
                <Box 
                  sx={{ 
                    mb: 3,
                    p: isMobile ? 1.5 : 2,
                    backgroundColor: "#f0fdfa",
                    borderRadius: "10px",
                    border: "1px solid #ccfbf1",
                    textAlign: "center"
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "13px" : "14px" }}>
                    Code sent to
                  </Typography>
                  <Typography variant="body1" fontWeight="600" sx={{ color: "#219297", mt: 0.5, fontSize: isMobile ? "14px" : "16px" }}>
                    {signupEmail}
                  </Typography>
                </Box>
                
                <Box mb={3}>
                  <label 
                    htmlFor="otp-input" 
                    style={{ 
                      fontSize: isMobile ? "13px" : "14px", 
                      fontWeight: "600", 
                      marginBottom: "8px", 
                      display: "block",
                      color: "#374151"
                    }}
                  >
                    Verification Code
                  </label>
                  <input
                    id="otp-input"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    required
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    style={{
                      width: "100%",
                      padding: isMobile ? "12px 14px" : "14px 16px",
                      fontSize: isMobile ? "16px" : "18px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "10px",
                      boxSizing: "border-box",
                      outline: "none",
                      transition: "all 0.2s ease",
                      fontFamily: "monospace",
                      letterSpacing: "0.3em",
                      textAlign: "center",
                      fontWeight: "600",
                      backgroundColor: "#fafafa",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#219297";
                      e.target.style.backgroundColor = "#ffffff";
                      e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.backgroundColor = "#fafafa";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={otpLoading}
                  sx={{
                    py: isMobile ? 1.25 : 1.75,
                    backgroundColor: "#219297",
                    color: "#ffffff",
                    borderRadius: "10px",
                    fontWeight: 600,
                    fontSize: isMobile ? "14px" : "15px",
                    textTransform: "none",
                    boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
                    "&:hover": {
                      backgroundColor: "#1a7a7d",
                      boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&:disabled": {
                      backgroundColor: "#cbd5e1",
                      boxShadow: "none",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  {otpLoading ? (
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
                      {isMobile ? "Verifying..." : "Verifying code..."}
                    </Box>
                  ) : (
                    "Verify & Continue"
                  )}
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setShowOtpField(false);
                    setOtp("");
                  }}
                  sx={{
                    mt: 2,
                    color: "#219297",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: isMobile ? "12px" : "14px",
                    "&:hover": {
                      backgroundColor: "#f0fdfa",
                    },
                  }}
                >
                  ← Change Email Address
                </Button>
              </Box>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            margin: isMobile ? "16px" : "32px",
            maxHeight: isMobile ? "calc(100% - 32px)" : "calc(100% - 64px)",
            display: "flex",
            flexDirection: "column",
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(4px)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 2 : 3,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            width: "100%",
            maxWidth: isMobile ? "calc(100vw - 32px)" : "500px",
            overflow: "hidden",
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          pt: isMobile ? 3 : 4, 
          px: isMobile ? 3 : 4,
          flexShrink: 0,
        }}>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
            <Box
              sx={{
                width: isMobile ? 48 : 64,
                height: isMobile ? 48 : 64,
                borderRadius: "50%",
                backgroundColor: "#e0f7f7",
                display: "flex",
                alignItems: "center",
                justifyContent:"center",
                mb: 2,
              }}
            >
              <Icon sx={{ fontSize: isMobile ? 24 : 32, color: "#219297" }}>
                lock
              </Icon>
            </Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ mb: 1, color: isDarkMode? "#ffffff":"#1a1a1a" }}>
              Welcome Back!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ 
              maxWidth: 380,
              fontSize: isMobile ? "13px" : "14px",
            }}>
              Sign in to access your dashboard
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          px: isMobile ? 3 : 4, 
          pb: isMobile ? 3 : 4, 
          pt: isMobile ? 1 : 3,
          overflow: "auto",
          flex: 1,
        }}>
          {renderLoginForm()}
        </DialogContent>
      </Dialog>
      
      <Modal
        open={addDomainModalOpen}
        onClose={() => setAddDomainModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: isMobile ? 1 : 2,
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: isMobile ? "calc(100vw - 32px)" : 500,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: isMobile ? 2.5 : 4,
            maxHeight: isMobile ? "calc(100vh - 32px)" : "auto",
            overflow: "auto",
          }}
        >
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" gutterBottom>
            Add New Domain
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3} sx={{ fontSize: isMobile ? "13px" : "14px" }}>
            Register a new domain with Ansera AI
          </Typography>

          <form onSubmit={handleAddDomainSubmit}>
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={12}>
                <Box mb={2}>
                  <label htmlFor="domainName" style={{ fontSize: isMobile ? "13px" : "14px", fontWeight: "500", marginBottom: "8px", display: "block" }}>
                    Website Domain Name
                  </label>
                  <input
                    id="domainName"
                    name="domainName"
                    placeholder="example.com"
                    value={domainFormData.domainName}
                    onChange={handleDomainFormChange}
                    required
                    style={{
                      width: "100%",
                      padding: isMobile ? "10px 12px" : "12px 14px",
                      fontSize: isMobile ? "13px" : "14px",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      boxSizing: "border-box"
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box mb={2}>
                  <label htmlFor="websitePlatform" style={{ fontSize: isMobile ? "13px" : "14px", fontWeight: "500", marginBottom: "8px", display: "block" }}>
                    Website Platform
                  </label>
                  <SoftSelect
                    value={domainFormData.websitePlatform}
                    options={websitePlatforms}
                    onChange={(value) => handleDomainSelectChange("websitePlatform", value)}
                    placeholder="Select Website Platform"
                    required
                    sx={{
                      width: "100%",
                      "& .MuiSelect-select": {
                        padding: isMobile ? "10px 12px" : "12px 14px",
                        fontSize: isMobile ? "13px" : "14px",
                      },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box mb={3}>
                  <label htmlFor="companyName" style={{ fontSize: isMobile ? "13px" : "14px", fontWeight: "500", marginBottom: "8px", display: "block" }}>
                    Company Name
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    placeholder="Ex: Sample Inc."
                    value={domainFormData.companyName}
                    onChange={handleDomainFormChange}
                    required
                    style={{
                      width: "100%",
                      padding: isMobile ? "10px 12px" : "12px 14px",
                      fontSize: isMobile ? "13px" : "14px",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      boxSizing: "border-box"
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={domainRegistrationLoading}
                  sx={{
                    py: isMobile ? 1.25 : 1.5,
                    backgroundColor: "#219297",
                    color: "#ffffff",
                    borderRadius: "8px",
                    border: "none",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    fontSize: isMobile ? "14px" : "15px",
                    "&:hover": {
                      backgroundColor: "#1a7a7d",
                    },
                    "&:disabled": {
                      backgroundColor: "#cccccc",
                      cursor: "not-allowed",
                    }
                  }}
                >
                  {domainRegistrationLoading ? (
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                      {isMobile ? "Adding..." : "Adding Domain..."}
                    </Box>
                  ) : (
                    "Add Domain"
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Modal>
    </>
  );
}

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;